import { useState } from "react";
import { Link } from "react-router-dom";
import { useCustomerFetch, Card, SectionLabel, StatusBadge, TICKET_STATUS_TONE, TICKET_STATUS_LABEL, requestStatusMeta, daysBetween } from "../../components/customer-app/ui";
import { CategoryTile } from "../../components/customer-app/CategoryTile";
import { CATEGORY_TILES, REFER_TILE } from "../../lib/categoryTiles";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../contexts/LanguageContext";
import CustomerPromoBanner from "../../components/customer-app/CustomerPromoBanner";
import {
  Wrench, ShoppingCart, Sun, ChevronRight, Zap, Clock, User, Users, ShieldAlert, ShieldCheck, ArrowRight,
  FileCheck2, Sparkles,
} from "lucide-react";

const CATEGORY_LABEL = { epc: "Rooftop Installation", product_enquiry: "Product Enquiry" };
const REFERRAL_STATUS_LABEL = {
  open: "Signed up", accepted: "In progress", on_hold: "On hold", dropped: "Closed", converted: "Converted!",
};
const CLOSED_INSTALL_STATUSES = new Set(["installation_completed", "closed"]);

function BannerCard({ c }) {
  const body = (
    <div
      data-testid={`home-banner-card-${c.key}`}
      className={
        "flex-shrink-0 w-[220px] snap-start rounded-2xl p-3.5 border-2 " +
        (c.kind === "reference" ? "bg-amber-50/70 border-amber-200" :
          c.installationDone ? "bg-emerald-50/80 border-emerald-300" :
          c.quotationPending ? "bg-amber-50/60 border-amber-300" : "bg-white border-slate-200/80")
      }
    >
      <div className="flex items-center justify-between">
        <span className={"inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md " + (c.kind === "reference" ? "bg-amber-200 text-amber-800" : "bg-emerald-100 text-emerald-700")}>
          {c.kind === "reference" ? <Users className="w-2.5 h-2.5" /> : <User className="w-2.5 h-2.5" />}
          {c.kind === "reference" ? "My Reference" : "Self"}
        </span>
        {c.serviceable === false ? (
          <span className="inline-flex items-center gap-1 text-[9px] font-bold text-red-700"><ShieldAlert className="w-2.5 h-2.5" /> Not Serviceable</span>
        ) : c.quotationPending ? (
          <span className="inline-flex items-center gap-1 text-[9px] font-bold text-amber-700"><FileCheck2 className="w-2.5 h-2.5" /> Quote Ready</span>
        ) : c.installationDone ? (
          <span className="inline-flex items-center gap-1 text-[9px] font-bold text-emerald-700"><ShieldCheck className="w-2.5 h-2.5" /> Installed</span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[9px] font-bold text-emerald-600"><ShieldCheck className="w-2.5 h-2.5" /> Serviceable</span>
        )}
      </div>
      <div className="text-sm font-bold text-slate-800 mt-2 truncate">{c.title}</div>
      <div className="text-[10px] text-slate-400 font-mono mt-0.5">{CATEGORY_LABEL[c.category] || c.category || ""}</div>
      {c.phase != null && (
        <div className="mt-2.5">
          <div className="flex items-center justify-between text-[10px] font-mono text-slate-500">
            <span>Phase {c.phase}/5</span><span>{c.pct}%</span>
          </div>
          <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <div className={"h-full rounded-full " + (c.installationDone ? "bg-emerald-500" : "bg-[#0D4721]")} style={{ width: `${c.pct}%` }} />
          </div>
        </div>
      )}
      <div className={"text-xs font-semibold mt-2 " + (c.quotationPending ? "text-amber-700" : "text-slate-600")}>{c.statusLabel}</div>
      {c.link && (
        <div className={"flex items-center gap-1 text-[11px] font-bold mt-2 " + (c.quotationPending ? "text-amber-700" : "text-[#0D4721]")}>
          {c.quotationPending ? "Review Quotation" : "Track Progress"} <ArrowRight className="w-3 h-3" />
        </div>
      )}
    </div>
  );
  return c.link ? <Link to={c.link}>{body}</Link> : body;
}

function StartSomethingNew() {
  return (
    <div data-testid="home-start-new">
      <div className="text-sm font-bold text-slate-700 mb-2">Nothing in progress — start something new</div>
      <div className="-mx-4 px-4 overflow-x-auto flex gap-2.5 snap-x snap-mandatory pb-1">
        {CATEGORY_TILES.map((t) => (
          <CategoryTile
            key={t.key}
            tile={t}
            variant="compact"
            to={`/app/buy-installation?mode=self&category=${t.key}`}
            testId={`start-new-${t.key}`}
          />
        ))}
        <CategoryTile
          tile={REFER_TILE}
          variant="compact"
          to="/app/buy-installation?mode=refer"
          testId="start-new-refer"
        />
      </div>
    </div>
  );
}

export default function CustomerHome() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [tab, setTab] = useState("self"); // self | references
  const { data: requests } = useCustomerFetch("/customer-app/requests");
  const { data: ibs } = useCustomerFetch("/customer-app/installed-base");
  const { data: tickets } = useCustomerFetch("/customer-app/tickets");
  const { data: referrals } = useCustomerFetch("/customer-app/referrals");

  const loading = !requests || !ibs || !tickets || !referrals;
  const latestIb = !loading ? [...ibs].sort((a, b) => new Date(b.commissioning_date) - new Date(a.commissioning_date))[0] : null;
  const recentTickets = !loading ? tickets.slice(0, 3) : [];

  const openRequests = !loading ? requests.filter((r) => !r.is_closed) : [];
  const pendingQuotations = !loading ? requests.filter((r) => r.quotation_pending) : [];

  const activeSelfCards = openRequests.map((r) => {
    const meta = requestStatusMeta(r);
    return {
      key: `${r.kind}-${r.req_id}`, kind: "self", title: r.name, category: r.category,
      serviceable: r.is_serviceable, statusLabel: meta.label, sortDate: r.updated_at || r.created_at,
      quotationPending: r.quotation_pending, installationDone: r.installation_done,
      phase: r.kind === "opportunity" ? r.current_phase : null,
      pct: r.kind === "opportunity" ? Math.round((r.activities_done / Math.max(r.activities_total, 1)) * 100) : null,
      link: r.quotation_pending ? r.quotation_link : r.link,
    };
  }).sort((a, b) => new Date(b.sortDate) - new Date(a.sortDate));

  const activeReferenceCards = !loading ? referrals.filter((r) =>
    r.status !== "dropped" && !CLOSED_INSTALL_STATUSES.has(r.installation_status)
  ).map((r) => ({
    key: `ref-${r.lead_id}`, kind: "reference", title: r.customer_name, category: r.lead_type,
    serviceable: r.is_serviceable, sortDate: r.created_at,
    statusLabel: r.installation_status_label && r.installation_status_label !== "Not Logged" ? r.installation_status_label : (REFERRAL_STATUS_LABEL[r.status] || r.status),
    phase: r.current_phase, pct: r.overall_pct,
    link: `/app/referrals/${r.lead_id}`,
  })) : [];

  const activeCards = tab === "self" ? activeSelfCards : activeReferenceCards;

  return (
    <div className="space-y-5 pb-4" data-testid="customer-home">
      <div data-testid="customer-home-greeting">
        <div className="font-brand text-[26px] font-semibold text-[#0D4721] leading-tight">
          {t("home.greeting")} {user?.name?.split(" ")[0] || "there"} <Sun className="inline w-5 h-5 text-amber-500 -mt-1" />
        </div>
        <p className="text-[12.5px] text-slate-500 mt-0.5">{t("home.customerSubtitle")}</p>
      </div>

      <CustomerPromoBanner />

      {/* Quotation-pending alert — the ONE place we make sure this can never be missed */}
      {!loading && pendingQuotations.length > 0 && (
        <Link to={pendingQuotations[0].quotation_link} data-testid="home-quotation-alert"
          className="block rounded-2xl p-4 bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 shadow-[0_4px_20px_-2px_rgba(217,119,6,0.35)] animate-pulse">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/25 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-extrabold text-white">Quotation ready — review now</div>
              <div className="text-[11px] text-white/85 truncate mt-0.5">
                {pendingQuotations[0].name}{pendingQuotations.length > 1 ? ` · +${pendingQuotations.length - 1} more` : ""}
              </div>
            </div>
            <ArrowRight className="w-4.5 h-4.5 text-white flex-shrink-0" />
          </div>
        </Link>
      )}

      {/* Self / My References toggle */}
      <div className="flex bg-slate-100 rounded-xl p-1" data-testid="home-tab-toggle">
        <button onClick={() => setTab("self")} data-testid="home-tab-self"
          className={"flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold transition-colors " + (tab === "self" ? "bg-white text-[#0D4721] shadow-sm" : "text-slate-500")}>
          <User className="w-3.5 h-3.5" /> Self {activeSelfCards.length > 0 && `(${activeSelfCards.length})`}
        </button>
        <button onClick={() => setTab("references")} data-testid="home-tab-references"
          className={"flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold transition-colors " + (tab === "references" ? "bg-white text-amber-700 shadow-sm" : "text-slate-500")}>
          <Users className="w-3.5 h-3.5" /> My References {activeReferenceCards.length > 0 && `(${activeReferenceCards.length})`}
        </button>
      </div>

      {/* Scroll banner — the ONLY place Installation Progress (Phase + Activity completion) is shown at a glance */}
      {loading ? (
        <div className="h-28 rounded-2xl bg-slate-100 animate-pulse" />
      ) : activeCards.length > 0 ? (
        <div className="-mx-4 px-4 overflow-x-auto flex gap-2.5 snap-x snap-mandatory pb-1" data-testid="home-scroll-banner">
          {activeCards.map((c) => <BannerCard key={c.key} c={c} />)}
        </div>
      ) : (
        <StartSomethingNew />
      )}

      {/* Live system / warranty status — separate from progress tracking above */}
      {latestIb && (
        <div className="rounded-2xl p-5 text-white shadow-[0_4px_20px_-2px_rgba(13,71,33,0.06)] bg-gradient-to-br from-[#0D4721] via-[#15803D] to-[#0B3C1C]" data-testid="hero-live">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-amber-300 font-bold flex items-center gap-1.5">
                <Zap className="w-3 h-3" /> System Live
              </div>
              <div className="text-2xl font-extrabold tracking-tight mt-1.5">Day {daysBetween(latestIb.commissioning_date)}</div>
              <div className="text-sm text-white/80 mt-1">of comprehensive warranty · {latestIb.ib_number}</div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-[10px] uppercase tracking-widest text-white/60">Warranty</div>
              <div className="text-lg font-bold text-amber-300">{latestIb.is_active ? `${latestIb.days_left}d left` : "Expired"}</div>
            </div>
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div>
        <SectionLabel className="mb-2">Quick Actions</SectionLabel>
        <div className="grid grid-cols-3 gap-2.5">
          <Link to="/app/raise-complaint" data-testid="quick-action-complaint" className="flex flex-col items-center gap-1.5 bg-white rounded-2xl border border-slate-200/80 py-4 hover:border-amber-300 transition-colors">
            <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center"><Wrench className="w-4.5 h-4.5 text-amber-600" /></div>
            <div className="text-[11px] font-semibold text-slate-700 text-center leading-tight">Raise a<br />Complaint</div>
          </Link>
          <Link to="/app/buy-installation" data-testid="quick-action-new-order" className="flex flex-col items-center gap-1.5 bg-white rounded-2xl border border-slate-200/80 py-4 hover:border-emerald-300 transition-colors">
            <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center"><ShoppingCart className="w-4.5 h-4.5 text-emerald-700" /></div>
            <div className="text-[11px] font-semibold text-slate-700 text-center leading-tight">New<br />Order</div>
          </Link>
          <Link to="/app/installations" data-testid="quick-action-installations" className="flex flex-col items-center gap-1.5 bg-white rounded-2xl border border-slate-200/80 py-4 hover:border-sky-300 transition-colors">
            <div className="w-9 h-9 rounded-xl bg-sky-100 flex items-center justify-center"><Sun className="w-4.5 h-4.5 text-sky-600" /></div>
            <div className="text-[11px] font-semibold text-slate-700 text-center leading-tight">My<br />Installations</div>
          </Link>
        </div>
      </div>

      {/* Recent requests */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <SectionLabel>Recent Requests</SectionLabel>
          <Link to="/app/tickets" className="text-[11px] font-bold text-[#0D4721]">View all</Link>
        </div>
        {loading ? (
          <div className="h-20 rounded-2xl bg-slate-100 animate-pulse" />
        ) : recentTickets.length === 0 ? (
          <Card className="p-4 text-sm text-slate-500 text-center">No requests yet — everything's running smoothly.</Card>
        ) : (
          <div className="space-y-2">
            {recentTickets.map((t) => (
              <Link key={t.id} to={`/app/tickets?open=${t.id}`} data-testid={`home-ticket-${t.ticket_number}`} className="block">
                <Card className="p-3.5 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-800 truncate">{t.title}</div>
                    <div className="text-[11px] text-slate-400 font-mono mt-0.5 flex items-center gap-1"><Clock className="w-3 h-3" />{t.ticket_number}</div>
                  </div>
                  <StatusBadge tone={TICKET_STATUS_TONE[t.status]}>{TICKET_STATUS_LABEL[t.status]}</StatusBadge>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
